import React from "react";
import { Icon } from "@iconify/react";
import PropTypes from "prop-types";
import "./ButtonWithIcon.scss";

/**
 * ButtonWithIcon
 *
 * New props:
 * - iconPosition: "left" | "right" | "only"  (default: left)
 * - color: string | "primary"|"secondary"|"success"|"danger"|"warning"|"info" (default: "primary")
 *     * If it's a named variant, the SCSS variant class will be used.
 *     * If it's a raw color string (starts with #, rgb, hsl or css color name), it will be applied inline.
 * - width: number | string (default: undefined) -> number becomes px, string used as-is ("200px", "50%", "auto")
 *
 * Backwards compatible: text prop still required, icon optional.
 */

const VARIANT_CLASSES = new Set([
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
  "outline",
  "submit-button",
  "cancel-button",
  "edit-button",
  "large",
  "small",
  "form-action",
  "icon-only",
  "text-only",
]);

const ButtonWithIcon = ({
  icon,
  text,
  onClick = () => {},
  className = "",
  disabled = false,
  type = "button",
  iconPosition = "left", // left | right | only
  color = "primary", // can be variant name or CSS color string
  width, // number or string
  style = {}, // allow custom inline styles to merge
  ariaLabel, // optional for accessibility (useful when icon-only)
}) => {
  // determine if color is a variant or a raw CSS color
  const isVariant = typeof color === "string" && VARIANT_CLASSES.has(color);
  const inlineStyles = { ...style };

  if (!isVariant && color) {
    // use inline background color (only if not a variant name)
    inlineStyles.backgroundColor = color;
  }

  if (width !== undefined && width !== null) {
    inlineStyles.width = typeof width === "number" ? `${width}px` : width;
  }

  // build class names
  const variantClass = isVariant ? ` ${color}` : "";
  const iconPosClass = iconPosition === "only" ? " icon-only" : iconPosition === "right" ? " icon-right" : " icon-left";
  const disabledClass = disabled ? " disabled" : "";

  const classes = `button-with-icon${variantClass}${iconPosClass}${disabledClass} ${className}`.trim();

  // Accessibility: if icon-only and no text, ensure aria-label provided
  const computedAriaLabel = ariaLabel || (icon && !text ? "button" : undefined);

  return (
    <button
      type={type}
      onClick={onClick}
      className={classes}
      disabled={disabled}
      style={inlineStyles}
      aria-label={computedAriaLabel}
      aria-disabled={disabled}
    >
      {/* Render icon left or only or right */}
      {icon && iconPosition !== "right" && (
        <Icon icon={icon} className="button-icon" aria-hidden={!!text} />
      )}

      {/* If icon-only, hide text visually (still available to screen readers if aria-label given) */}
      {iconPosition !== "only" && <span className="button-text">{text}</span>}

      {icon && iconPosition === "right" && (
        <Icon icon={icon} className="button-icon" aria-hidden={!!text} />
      )}
    </button>
  );
};

ButtonWithIcon.propTypes = {
  icon: PropTypes.string,
  text: PropTypes.string, // not required if iconPosition === 'only' and ariaLabel provided
  onClick: PropTypes.func,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  iconPosition: PropTypes.oneOf(["left", "right", "only"]),
  color: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  style: PropTypes.object,
  ariaLabel: PropTypes.string,
};

ButtonWithIcon.defaultProps = {
  onClick: () => {},
  className: "",
  disabled: false,
  type: "button",
  iconPosition: "left",
  color: "primary",
  width: undefined,
  style: {},
  ariaLabel: undefined,
};

export default ButtonWithIcon;
